import getDesignDataForExport from 'editor/src/store/design/selector/getDesignDataForExport';
import type { DesignData } from 'editor/src/store/design/types';
import type { Addon } from 'editor/src/store/editorModules/addons/types';
import { ImageState, type GalleryImage } from 'editor/src/store/gallery/types';

import { captureException } from 'editor/src/logger';

import ConfigurationSession from './ConfigurationSession';

import type { PersonalizationPricingCapability } from './types';

interface DesignStore {
  getState(): {
    design: { designData?: DesignData };
    gallery: { images: GalleryImage[] };
    editorModules: { addons: { inUse: Addon[] } };
  };
  subscribe(listener: () => void): () => void;
}

interface PricingRuntime {
  session: ConfigurationSession;
  previousDesign?: DesignData;
  previousImages?: GalleryImage[];
  previousAddons?: Addon[];
}

// Thunks receive the same getState function as their store, even when wrapped in a temporary object.
const runtimes = new WeakMap<DesignStore['getState'], PricingRuntime>();
function getRuntime(store: Pick<DesignStore, 'getState'>): PricingRuntime {
  let runtime = runtimes.get(store.getState);
  if (!runtime) {
    runtime = { session: new ConfigurationSession() };
    runtimes.set(store.getState, runtime);
  }
  return runtime;
}

export function getPersonalizationPricingSession(store: Pick<DesignStore, 'getState'>) {
  return getRuntime(store).session;
}

export function refreshPersonalizationPricing(store: Pick<DesignStore, 'getState'>) {
  const runtime = getRuntime(store);
  const personalizationPricingSession = runtime.session;
  if (personalizationPricingSession.getState().status === 'disabled') {
    return;
  }
  const state = store.getState();
  const design = state.design.designData;
  if (!design) {
    personalizationPricingSession.invalidate();
    return;
  }
  if (
    design === runtime.previousDesign &&
    state.gallery.images === runtime.previousImages &&
    state.editorModules.addons.inUse === runtime.previousAddons
  ) {
    return;
  }
  runtime.previousDesign = design;
  runtime.previousImages = state.gallery.images;
  runtime.previousAddons = state.editorModules.addons.inUse;
  if (state.gallery.images.some((image) => image.state !== ImageState.UPLOADED)) {
    personalizationPricingSession.invalidate();
    return;
  }
  try {
    personalizationPricingSession.update(
      getDesignDataForExport(design, state.gallery.images, state.editorModules.addons.inUse, []),
    );
  } catch (error) {
    // Runs inside a store subscriber, where an escaping throw would surface from an unrelated
    // dispatch. Blocking submission is the protection; Sentry keeps the diagnostics.
    personalizationPricingSession.fail('The design could not be prepared for pricing.');
    captureException(error, { extra: { personalizationPricing: 'export' } });
  }
}

export function configurePersonalizationPricing(store: DesignStore, capability?: PersonalizationPricingCapability) {
  const runtime = getRuntime(store);
  runtime.previousDesign = undefined;
  runtime.previousImages = undefined;
  runtime.previousAddons = undefined;
  runtime.session.configure(capability);
  refreshPersonalizationPricing(store);
}

// refreshPersonalizationPricing already short-circuits on an unchanged design, gallery and addon
// set, so the subscriber hands it every notification rather than comparing the same three again.
export function watchPersonalizationPricing(store: DesignStore) {
  return store.subscribe(() => refreshPersonalizationPricing(store));
}
